import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, reviewData } from '@/mocks/movies';

export default function AboutPage() {
  const location = useLocation();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于51影院',
    description: siteInfo.description,
    mainEntity: {
      '@type': 'Organization',
      name: '51影院',
      url: 'https://www.51cinema.com',
    },
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9nj65cit35ii3jdls0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed as Record<string, string> | null)?.message
          || (parsed as Record<string, Record<string, string>> | null)?.meta?.message
          || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试。');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            {/* Hero */}
            <section className="px-4 md:px-6 py-10 md:py-14 text-center">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">关于51影院</h1>
              <p className="text-foreground-600 max-w-3xl mx-auto text-sm md:text-base leading-relaxed">
                51影院是一个专注于免费高清影视资源聚合的平台，汇聚<strong>6080影院</strong>、<strong>小a影院</strong>、<strong>8090影院</strong>、<strong>4480影院</strong>等优质资源站的影视内容，
                致力于为广大影迷提供超清画质、免广告、不缓冲的流畅在线观影体验。
              </p>
            </section>

            {/* Stats */}
            <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100" aria-label="平台数据">
              <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                  {[
                    { value: '50,000+', label: '影视资源', icon: 'ri-movie-line' },
                    { value: '99.9%', label: '播放成功率', icon: 'ri-check-double-line' },
                    { value: '100万+', label: '每日访问用户', icon: 'ri-user-line' },
                    { value: '24h', label: '资源更新速度', icon: 'ri-timer-flash-line' },
                  ].map((stat, idx) => (
                    <div key={idx} className="text-center p-5 bg-background-50 rounded-lg">
                      <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                        <i className={`${stat.icon} text-lg`}></i>
                      </div>
                      <div className="text-2xl md:text-3xl font-bold font-heading text-primary-500 mb-1">{stat.value}</div>
                      <div className="text-xs text-foreground-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Brand Story */}
            <section className="px-4 md:px-6 py-10 md:py-14" aria-label="品牌故事">
              <div className="max-w-4xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-8 md:gap-10 items-center">
                  <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">品牌故事</h2>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-3">
                      51影院诞生于对优质影视内容的追求。我们发现，在互联网上有许多优秀的影视资源站，但影迷们往往需要辗转多个网站才能找到想看的影片。
                    </p>
                    <p className="text-foreground-700 text-sm leading-relaxed mb-3">
                      于是，我们决定打造一个聚合平台——将<strong>6080影院</strong>、<strong>小a影院</strong>、<strong>8090影院</strong>、<strong>4480影院</strong>
                      等优质资源整合在一起，让用户在一个平台上就能找到所有想看的影视内容。
                    </p>
                    <p className="text-foreground-700 text-sm leading-relaxed">
                      我们始终坚持"免费、高清、无广告"的理念，不断优化平台体验。从最初的简单聚合，到如今覆盖电影、电视剧、综艺、动漫全品类，
                      51影院已经成为数百万影迷的首选观影平台。
                    </p>
                  </div>
                  <div className="flex-1 w-full">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20modern%20stylish%20home%20theater%20room%20with%20a%20large%20projector%20screen%20displaying%20colorful%20abstract%20cinematic%20visuals%2C%20comfortable%20seating%2C%20warm%20ambient%20lighting%2C%20movie%20posters%20on%20the%20wall%2C%20cozy%20and%20inviting%20atmosphere%2C%20cinematic%20aesthetic%2C%20no%20people&width=800&height=500&seq=about-cinema-01&orientation=landscape"
                      alt="51影院品牌故事"
                      className="w-full rounded-lg object-cover h-56 md:h-72"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="用户口碑">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-2 text-center">用户口碑</h2>
                <p className="text-foreground-600 text-sm text-center mb-8">听听用户们怎么说</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {reviewData.map((review, idx) => (
                    <div key={idx} className="bg-background-50 rounded-lg p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center font-bold text-secondary-700 text-sm">
                          {review.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium text-foreground-900 text-sm">{review.name}</div>
                          <div className="text-accent-500 text-xs">
                            {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                          </div>
                        </div>
                      </div>
                      <p className="text-foreground-600 text-sm leading-relaxed">{review.content}</p>
                      <p className="text-foreground-400 text-xs mt-3">{review.date}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact Form */}
            <section className="px-4 md:px-6 py-10 md:py-14" aria-label="联系我们">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-2 text-center">联系我们</h2>
                <p className="text-foreground-600 text-sm text-center mb-8">有任何问题或建议，欢迎联系我们</p>

                <form id="contact-form" data-readdy-form="" onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot */}
                  <div className="spam-guard" aria-hidden="true">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>

                  <div className="text-center">
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="px-8 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                    </button>
                  </div>

                  {formStatus === 'success' && (
                    <div className="text-center text-sm text-green-600 bg-green-50 py-3 px-4 rounded-lg">
                      感谢您的留言，我们会尽快回复！
                    </div>
                  )}
                  {formStatus === 'error' && formError && (
                    <div className="text-center text-sm text-red-600 bg-red-50 py-3 px-4 rounded-lg">
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </section>

            {/* Contact Info */}
            <section className="px-4 md:px-6 py-6 md:py-10 bg-background-100" aria-label="联系方式">
              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                  <div className="text-center p-5">
                    <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <h3 className="font-medium text-foreground-900 text-sm mb-1">邮箱</h3>
                    <p className="text-foreground-600 text-xs">{siteInfo.email}</p>
                  </div>
                  <div className="text-center p-5">
                    <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <h3 className="font-medium text-foreground-900 text-sm mb-1">电话</h3>
                    <p className="text-foreground-600 text-xs">{siteInfo.phone}</p>
                  </div>
                  <div className="text-center p-5">
                    <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <h3 className="font-medium text-foreground-900 text-sm mb-1">地址</h3>
                    <p className="text-foreground-600 text-xs">{siteInfo.address}</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}